import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useCancelAccuracyJobMutation } from '../../../generate-accuracy/api/accuracyApiSlice';
import { buildAccuracyDashboardUrl } from '../../emitters/buildAccuracyDashboardUrl';
import {
  useAccuracyRunHistory,
  type AccuracyRunView,
  type UseAccuracyRunHistoryResult,
} from '../../hook/useAccuracyRunHistory';

interface UseAccuracyRunsModalArgs {
  open: boolean;
  projectId: string | null;
  projectName: string | null;
  onClose: () => void;
  onNewRun: (projectId: string) => void;
}

export interface UseAccuracyRunsModalResult {
  runs: AccuracyRunView[];
  isLoading: boolean;
  error: UseAccuracyRunHistoryResult['error'];
  refetch: UseAccuracyRunHistoryResult['refetch'];
  isCancelPending: boolean;
  cancelError: boolean;
  dismissCancelError: () => void;
  subtitle: string;
  handleClose: () => void;
  handleNewRun: () => void;
  handleViewResults: (view: AccuracyRunView) => void;
  handleCancel: (view: AccuracyRunView) => void;
}

/**
 * Owns everything non-visual for the Accuracy runs modal: run-history fetching
 * (via `useAccuracyRunHistory`), the cancel mutation and its error flag,
 * deep-link navigation to the dashboard, and the close / new-run handlers.
 * `AccuracyRunsModal` only renders what this returns.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const useAccuracyRunsModal = ({
  open,
  projectId,
  projectName,
  onClose,
  onNewRun,
}: UseAccuracyRunsModalArgs): UseAccuracyRunsModalResult => {
  const { formatMessage } = useIntl();

  const { runs, totalRunCount, isLoading, error, refetch } = useAccuracyRunHistory(
    open ? projectId : null
  );
  const [cancelJob, { isLoading: isCancelPending }] = useCancelAccuracyJobMutation();
  const [cancelError, setCancelError] = useState(false);

  const handleClose = () => {
    setCancelError(false);
    onClose();
  };

  const handleNewRun = () => {
    if (projectId) {
      onNewRun(projectId);
    }
  };

  const handleViewResults = (view: AccuracyRunView) => {
    const url = buildAccuracyDashboardUrl(view.run);
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  };

  const handleCancel = (view: AccuracyRunView) => {
    setCancelError(false);
    cancelJob(view.run.accuracyJobId)
      .unwrap()
      .catch(() => setCancelError(true));
  };

  const subtitle = projectName
    ? formatMessage(
        { id: 'adminProjects.accuracyRunsModal.subtitle' },
        { projectName, count: totalRunCount }
      )
    : formatMessage(
        { id: 'adminProjects.accuracyRunsModal.subtitleNoName' },
        { count: totalRunCount }
      );

  return {
    runs,
    isLoading,
    error,
    refetch,
    isCancelPending,
    cancelError,
    dismissCancelError: () => setCancelError(false),
    subtitle,
    handleClose,
    handleNewRun,
    handleViewResults,
    handleCancel,
  };
};
